import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import DownloadIcon from '@mui/icons-material/Download';
import ReactPDF from '@react-pdf/renderer';
import Button from '@material-ui/core/Button';
import Card from '@mui/material/Card';
import EvStationIcon from '@mui/icons-material/EvStation';
import TimerIcon from '@mui/icons-material/Timer';
import EuroIcon from '@mui/icons-material/Euro';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@material-ui/core';
import moment, { Duration } from 'moment';
import {
  AlertColor, Backdrop, Fade, Modal,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import PhoneNumber, { getRegionCodeForCountryCode, parsePhoneNumber } from 'awesome-phonenumber';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@mui/material/Alert';
import Checkbox from '@material-ui/core/Checkbox';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { useParams, useHistory } from 'react-router';
import { DocumentInfo, SessionInfo, TaxDetails } from '../types/types';
import server, {
  closeSession, getDocuments, getTaxDetails, saveTaxDetails,
} from '../api/api';
import PdfBuilder from './PdfBuilder';
import i18n from '../i18n/i18n';
import { FormContainer, StyledTextField } from '../components/forms/FormStyles';
import { useTheme } from '../contexts/ThemeContext';
import { countryData } from '../constants/CountryData';
import { getVatNumberError } from '../utils/vatValidation';
import { getPostalCodeError } from '../utils/postalCodeValidation';
import LogoFooter from '../LogoFooter';
import TopHeader from '../components/TopHeader';
import PowerChart from '../components/charts/PowerChart';
import TaxCountryModal from '../components/modals/TaxCountryModal';
import helexiaChargingIcon from '../images/helexia_charging_icon.svg';
import helexiaDurationIcon from '../images/helexia_duration_icon.svg';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-bottom: 80px;
`;

const DocumentsContainer = styled.div`
  align-self: center;
  width: 100%;
  margin-top: 10px;
`;

const Message = styled.h1`
  text-align: center;
  margin-top: 2.5em;
`;

const HeaderName = styled.h1<{ font?: string }>`
  position: sticky;
  top: 0;
  z-index: 1000;

  display: flex;
  justify-content: center;
  align-items: center;

  font: 600 1.6em ${(props) => props.font};
  color: ${(props) => props.color};
  text-align: center;

  width: 100%;
  box-sizing: border-box;

  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  padding: 14px 10px;
  margin: 0;

  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);

  font-size: clamp(1.1em, 4dvw, 1.6em);
  line-height: 1.3;
  letter-spacing: 0.5px;
`;

const StyledSpanPin = styled.span<{ font?: string }>`
  font: bold 1.2em ${(props) => props.font};
  color: ${(props) => props.color};
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
`;

const StyledButton = styled(({ ...rest }) => (
  <Button
    {...rest}
  />
))`
  && {
    background-color: ${(props) => props.color};
    color: ${(props) => props.textColor};
    border-width: 0;
    border-radius: 10px;
    padding: 10px 20px;
    margin: 10px;
    width: 300px;
    text-transform: capitalize;
    font: bold 0.85em ${(props) => props.font};
    &:active {
      background-color: ${(props) => props.color};
    }

    &:focus {
      background-color: ${(props) => props.color};
    }
  }

  &&:disabled,
  &&.ant-btn-disabled {
    background-color: #d9d9d9;
    color: #8c8c8c;
    cursor: not-allowed;
    opacity: 1;
  }
`;

const StyledButton2 = styled(({ ...rest }) => (
    <Button {...rest} />
))`
  && {
    background-color: ${(props) => props.color};
    color: ${(props) => props.textColor};
    border-width: 0;
    border-radius: 10px;
    padding: 10px 20px;
    margin: 10px;
    width: 120px;
    text-transform: capitalize;
    font: bold 0.85em ${(props) => props.font};
  }

  &&:disabled,
  &&.ant-btn-disabled {
    background-color: #d9d9d9;
    color: #8c8c8c;
    cursor: not-allowed;
    opacity: 1;
  }
`;

const LoadingWrapper = styled.div`
  animation: rotate 2s linear infinite;
  
  @keyframes rotate {
    0% {
    transform: rotate(0deg);
    }
    100% {
    transform: rotate(360deg);
    }
  }
`;

const StyledLoading = styled.img`
  clip-path: polygon(50% 50%, 100% 0, 0 0);
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4%;
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  width: 25%;
  height: 100px;
  padding: 10px;
`;

const StyledSpan = styled.span<{ font?: string }>`
  color: ${(props) => props.color};
  font-family: ${(props) => props.font};
  margin-top: 20px;
  font-weight: bold;
`;

const StyledP = styled.span<{ font?: string }>`
  color: ${(props) => props.color};
  font-family: ${(props) => props.font};
  margin-top: 1px;
`;

const StyledDiv = styled.div`
  min-height: 10dvh;
`;

const HelexiaInputWrapper = styled.div`
  background: #efefef;
  border-radius: 12px;
  height: 36px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  gap: 4px;
  flex-shrink: 0;
`;

const HelexiaInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #3f3f3f;
  font-family: Geologica, sans-serif;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -0.98px;
  width: 100%;
  &::placeholder {
    color: #6a6a6a;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #efefef inset;
    box-shadow: 0 0 0px 1000px #efefef inset;
    -webkit-text-fill-color: #3f3f3f;
  }
`;

const HelexiaSpinningCircle = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 6px solid transparent;
  border-top-color: #f0ca25;
  border-right-color: #f0ca25;
  border-bottom-color: #f0ca25;
  animation: helexia-spin 1.4s linear infinite;

  @keyframes helexia-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

type Error = {
  nif: string | null,
  mobilePhone: string | null,
  email: string | null,
  streetName: string | null,
  zipCode: string | null,
  city: string | null,
  name: string | null,
};

const DOCUMENTS_POLL_INTERVAL_MS = 5000;
const DOCUMENTS_POLL_MAX_ATTEMPTS = 24;

interface ChargingData {
  chargingTime?: number | null;
  kWh?: number | null;
  price?: number | null;
  powerProfile?: { time: number; power: number }[];
}

const SessionDocumentsView = () => {
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [loadingTerminate, setLoadingTerminate] = useState(false);
  const [showTaxpayerModal, setShowTaxpayerModal] = useState(false);
  const [error, setError] = useState<Error>({
    nif: null,
    mobilePhone: null,
    email: null,
    streetName: null,
    zipCode: null,
    city: null,
    name: null,
  });
  const [name, setName] = useState('');
  const [country, setCountry] = useState('PT');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [vatNumber, setVatNumber] = useState({
    prefix: countryData.PT?.prefix || '',
    number: '',
  });
  const [email, setEmail] = useState('');
  const [streetName, setStreetName] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [city, setCity] = useState('');
  const [taxpayerSaveLoading, setTaxpayerSaveLoading] = useState(false);
  const [taxpayerLoading, setTaxpayerLoading] = useState(true);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [notificationClass, setNotificationClass] = useState('success');
  const telephoneNumber = new PhoneNumber(phoneNumber || '');
  const [time, setTime] = useState<Duration | null>(null);
  const [chartData, setChartData] = useState<any[] | null>(null);
  const [chargingData, setChargingData] = useState<ChargingData | null>(null);
  const [, setBackupTaxDetails] = useState<TaxDetails | null>({
    name: '',
    taxId: '',
    email: '',
    mobilePhone: '',
    streetName: '',
    zipCode: '',
    city: '',
  });
  const [isSelectingCountry, setSelectingCountry] = useState(false);
  const [countryRegionCode, setCountryRegionCode] = useState(countryData[country]?.code);
  const isAddressRequired = sessionInfo?.requiredFields?.includes('address') || false;

  const CountryFlag = countryData[country]?.component;

  const { theme, themeName, handleTheme } = useTheme();
  const isHelexia = themeName === 'helexia';
  const isGalp = themeName === 'galp';
  const history = useHistory();
  const [galpLang, setGalpLang] = useState<string>(i18n.language);
  const [galpLangOpen, setGalpLangOpen] = useState<boolean>(false);
  const [galpBillingOpen, setGalpBillingOpen] = useState<boolean>(false);

  const handleGalpLangChange = (lng: string) => {
    setGalpLang(lng);
    i18n.changeLanguage(lng);
    localStorage.setItem('pay.elecctro.language', lng);
    setGalpLangOpen(false);
  };

  const [saveToStorage, setSaveToStorage] = useState(() => localStorage.getItem('billInformation') !== null);

  const documentsPollAttempts = useRef(0);

  // @ts-ignore
  const { id: sessionId } = useParams();
  const isEVRelated = sessionInfo?.vendingMachine?.equipmentType?.label === 'EV Charger'
      || sessionInfo?.vendingMachine?.equipmentType?.label === 'EV Station';

  const handleCountrySelected = useCallback((selectedCountry) => {
    setCountry(selectedCountry);
    setCountryRegionCode(getRegionCodeForCountryCode(countryData[selectedCountry]?.code));
    setVatNumber({ prefix: countryData[selectedCountry]?.prefix, number: '' });
    setPhoneNumber('');
    setSelectingCountry(false);
  }, []);

  const showError = () => {
    setNotificationClass('error');
    setNotificationVisible(true);
  };

  const fetchDocuments = async (id: string) => {
    try {
      const response = await getDocuments(id);
      setSessionInfo(response);
      handleTheme(response.company.tag);
    } catch (err: any) {
      showError();
    }
  };

  const populateFromStoredData = (data: any) => {
    setName(data.name || '');
    setEmail(data.email || '');
    setStreetName(data.streetName || '');
    setZipCode(data.zipCode || '');
    setCity(data.city || '');

    if (data.mobilePhone) {
      const parsed = parsePhoneNumber(`+${data.mobilePhone}`);
      setPhoneNumber(parsed.getNumber() || '');
    }

    if (data.taxId) {
      const prefix = data.taxId.prefix || countryData.PT.prefix;
      const number = data.taxId.number || '';
      setCountry(prefix);
      setVatNumber({ prefix, number });
    } else {
      setVatNumber({ prefix: countryData.PT.prefix, number: '' });
    }

    setBackupTaxDetails(data);
    setTaxpayerLoading(false);
  };

  const fetchTaxDetails = async (id: string) => {
    try {
      // Load data from storage if available
      const storedData = localStorage.getItem('billInformation');
      const parsedStoredData = storedData ? JSON.parse(storedData) : null;

      if (parsedStoredData) {
        setSaveToStorage(true);
      }

      const response : TaxDetails | undefined = await getTaxDetails(id);

      // If API response is missing, but there's stored data, populate from stored data and exit early
      if (!response && parsedStoredData) {
        populateFromStoredData(parsedStoredData);
        return;
      }

      // If no response and no stored data, reset taxpayer details and exit early
      if (!response) {
        setBackupTaxDetails(null);
        setTaxpayerLoading(false);
        return;
      }

      setBackupTaxDetails(response);

      const prefix = response.taxId?.slice(0, 2) || parsedStoredData?.taxId.slice(0, 2);
      const number = response.taxId?.slice(2) || parsedStoredData?.taxId.slice(2);
      setCountry(prefix);
      setVatNumber({ prefix, number });

      const parsed = parsePhoneNumber(`+${response?.mobilePhone || parsedStoredData?.mobilePhone}`);
      setPhoneNumber(parsed.getNumber('international') || '');

      setName(response?.name || parsedStoredData?.name || '');
      setEmail(response?.email || parsedStoredData?.email || '');
      setStreetName(response?.streetName || parsedStoredData?.streetName || '');
      setZipCode(response?.zipCode || parsedStoredData?.zipCode || '');
      setCity(response?.city || parsedStoredData?.city || '');

      setTaxpayerLoading(false);
    } catch (err: any) {
      setTaxpayerLoading(false);
      showError();
    }
  };

  const handleSessionUpdate = (newInfo) => {
    setSessionInfo((prevState: any) => ({
      ...prevState,
      state: newInfo.sessionState,
      extra: {
        ...prevState.extra,
        ...(newInfo.update && { chargingData: newInfo.update as ChargingData }),
      },
    }));

    if (newInfo.state === 300) {
      setLoadingTerminate(false);
    }
  };

  const closeTaxpayerModal = () => {
    setShowTaxpayerModal(false);
    setGalpBillingOpen(false);
    setError({
      nif: null,
      mobilePhone: null,
      email: null,
      streetName: null,
      zipCode: null,
      city: null,
      name: null,
    });
    setPhoneNumber('');
    setVatNumber({ prefix: '', number: '' });
    setName('');
    setEmail('');
    setStreetName('');
    setZipCode('');
    setCity('');
  };

  const openTaxpayerModal = async () => {
    setTaxpayerLoading(true);
    setShowTaxpayerModal(true);
    await fetchTaxDetails(sessionId);
  };

  // Galp: the billing/NIF details form is a full screen (design 518-2478), not a modal.
  const openGalpBilling = async () => {
    setTaxpayerLoading(true);
    setGalpBillingOpen(true);
    await fetchTaxDetails(sessionId);
  };

  const onCloseAlert = () => setNotificationVisible(false);
  const getChangedFields = (newFields): TaxDetails => {
    const mutableFields = { ...newFields };

    // replace newFileds empty strings with null
    Object.keys(mutableFields).forEach((key) => {
      if (mutableFields[key] === '') {
        mutableFields[key] = null;
      }
    });

    return mutableFields as TaxDetails;
  };

  const saveTaxpayerDetails = async () => {
    // run address validation here
    if (!error.nif && !error.mobilePhone && !error.email && !error.streetName && !error.zipCode && !error.city) {
      setTaxpayerSaveLoading(true);
      /* const isValidAddress = await validateAddress(
        streetName,
        zipCode,
        city,
        countryRegionCode,
      );
      console.log(isValidAddress); */
      try {
        let formattedPhoneNumber;
        if (phoneNumber) {
          const parsedPhoneNumber = parsePhoneNumber(phoneNumber, countryRegionCode);
          formattedPhoneNumber = parsedPhoneNumber.getNumber().replace('+', '');
        }
        const taxDetails = getChangedFields({
          taxId: vatNumber.prefix + vatNumber.number,
          email,
          name,
          mobilePhone: formattedPhoneNumber,
          streetName,
          zipCode,
          city,
        });
        await saveTaxDetails(sessionId, taxDetails);
        setTaxpayerSaveLoading(false);
        setNotificationClass('success');
        setNotificationVisible(true);
      } catch (err: any) {
        setTaxpayerSaveLoading(false);
        showError();
      }

      closeTaxpayerModal();
    }
  };

  const handleSaveToStorage = useCallback(() => {
    if (!saveToStorage) {
      localStorage.removeItem('billInformation');
    }

    if (saveToStorage && !taxpayerLoading) {
      const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
      const formattedPhoneNumber = parsedPhoneNumber.getNumber()?.replace('+', '');
      const data = {
        taxId: vatNumber,
        email,
        mobilePhone: formattedPhoneNumber,
        streetName,
        zipCode,
        city,
        name,
      };

      localStorage.setItem('billInformation', JSON.stringify(data));
    }
  }, [vatNumber, email, telephoneNumber, saveToStorage]);

  const isFormInvalid = () => {
    if (taxpayerSaveLoading) return true;

    const requiredFields = isAddressRequired && (
      !name.trim()
      || !streetName.trim()
      || !zipCode.trim()
      || !city.trim()
    );

    return !!error.nif
      || !!getVatNumberError(vatNumber.prefix, vatNumber.number)
      || !!error.mobilePhone
      || !!error.email
      || !!error.streetName
      || !!error.zipCode
      || !!error.city
      || !!error.name
      || requiredFields;
  };

  useEffect(() => {
    if (showTaxpayerModal || galpBillingOpen) {
      handleSaveToStorage();
    }
  }, [handleSaveToStorage]);

  useEffect(() => {
    fetchDocuments(sessionId);
  }, []);

  useEffect(() => {
    if (isEVRelated) {
      server.onSocket('session_update', handleSessionUpdate);
    }

    setChargingData(sessionInfo?.extra?.chargingData as ChargingData);
    setTime(sessionInfo?.extra?.chargingData?.chargingTime != null ? moment.duration(sessionInfo.extra.chargingData.chargingTime, 'seconds') : null);

    if (sessionInfo?.extra?.chargingData?.powerProfile) {
      setChartData(
        (sessionInfo?.extra?.chargingData?.powerProfile as any[] | undefined)?.map((item: any) => {
          const timeInMinutes = Math.round(item.time / 60);
          return {
            time: timeInMinutes,
            power: item.power,
          };
        }) || null,
      );
    }

    return () => {
      server.offSocket('session_update', handleSessionUpdate);
    };
  }, [sessionInfo]);

  useEffect(() => {
    if (!sessionInfo) return undefined;
    if (sessionInfo.documents.length > 0) return undefined;
    if (documentsPollAttempts.current >= DOCUMENTS_POLL_MAX_ATTEMPTS) return undefined;

    const handle = setInterval(() => {
      documentsPollAttempts.current += 1;
      fetchDocuments(sessionId);
      if (documentsPollAttempts.current >= DOCUMENTS_POLL_MAX_ATTEMPTS) {
        clearInterval(handle);
      }
    }, DOCUMENTS_POLL_INTERVAL_MS);

    return () => clearInterval(handle);
  }, [sessionInfo, sessionId]);

  if (!sessionInfo) {
    return (
      <Root>
        <CircularProgress
          size={200}
          sx={{
            marginTop: '10px',
            marginBottom: '30px',
            color: theme.loadingIconColor,
            animationDuration: '80000ms',
          }}
          thickness={1}
          variant="indeterminate"
        />
      </Root>
    );
  }

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80dvw',
    minHeight: '50dvh',
    bgcolor: 'background.paper',
    border: `2px solid ${theme.buttonColor ? theme.buttonColor : '#f0b239'}`,
    borderRadius: '10px',
    boxShadow: 24,
    p: 4,
  };

  const canEditTaxDetails = sessionInfo?.canReissueTaxDocuments;

  const isCharging = [100, 0].includes(sessionInfo?.state);

  const handleDocumentDownload = async (documentInfo: DocumentInfo) => {
    let url: string;
    if (documentInfo?.url) {
      url = documentInfo.url;
    } else if (documentInfo?.text?.includes(' ')) {
      try {
        const pdfDocument = await ReactPDF.pdf(<PdfBuilder document={documentInfo} />).toBlob();
        url = window.URL.createObjectURL(pdfDocument);
      } catch {
        return;
      }
    } else {
      try {
        const byteCharacters = atob(documentInfo.text);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        url = URL.createObjectURL(blob);
      } catch {
        return;
      }
    }
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', i18n.t('document'));
    link.click();
    link.remove();
  };

  if (isGalp) {
    const galpLanguages = [
      { value: 'ptPT', code: 'PT', flag: '/companies/GALP/lang_flag_pt.svg' },
      { value: 'esES', code: 'ES', flag: '/companies/GALP/lang_flag_es.svg' },
      { value: 'enGB', code: 'EN', flag: '/companies/GALP/lang_flag_en.svg' },
    ];
    const currentLang = galpLanguages.find((l) => l.value === galpLang) || galpLanguages[0];

    const galpCardStyle: React.CSSProperties = {
      flex: 1,
      border: '1px solid #e2ddd6',
      borderRadius: '10px',
      padding: '8px 10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '4px',
      boxSizing: 'border-box',
      minWidth: 0,
    };
    const galpCardLabel: React.CSSProperties = {
      fontFamily: theme.bodyFont, fontSize: '11px', fontWeight: 500, color: '#616061', lineHeight: '16px',
    };
    const galpCardValue: React.CSSProperties = {
      fontFamily: theme.bodyFont, fontSize: '11px', fontWeight: 500, color: '#111110', lineHeight: '16px',
    };
    const durationText = time !== null
      ? `${Math.floor(time.asHours())}h${String(time.minutes()).padStart(2, '0')}m`
      : '---';

    // Footer document buttons (design 519-3291): authorization + payment + invoice.
    // NOTE: real document `type` strings are a backend contract not verified from the
    // client. CUSTOMER→payment is confirmed by other branches; /author/→authorization
    // and else→invoice are assumptions — adjust once the backend types are confirmed.
    const isAuthDoc = (t?: string) => /author/i.test(t || '');
    const galpDocLabel = (t?: string): string => {
      if (isAuthDoc(t)) return i18n.t('authorization_receipt');
      if (t === 'CUSTOMER') return i18n.t('payment_document');
      return i18n.t('invoice_download');
    };
    const galpDocs: DocumentInfo[] = sessionInfo?.documents || [];
    const galpAuthDoc = galpDocs.find((d) => isAuthDoc(d.type)) || galpDocs[0];
    const galpDocRank = (t?: string): number => {
      if (isAuthDoc(t)) return 0;
      return t === 'CUSTOMER' ? 1 : 2;
    };
    const galpOrderedDocs = [...galpDocs].sort((a, b) => galpDocRank(a.type) - galpDocRank(b.type));
    const galpDocBtnStyle: React.CSSProperties = {
      height: '36px',
      borderRadius: '57px',
      background: 'white',
      border: '1px solid #bab9ba',
      color: '#111110',
      fontFamily: theme.bodyFont,
      fontSize: '14px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    };
    const renderGalpDocButton = (key: string, label: string, onDownload: () => void) => (
      <button key={key} type="button" onClick={onDownload} style={galpDocBtnStyle}>
        <img
          src="/companies/GALP/receipt_download_icon.svg"
          alt=""
          style={{ width: '12px', height: '12px', display: 'block' }}
        />
        {label}
      </button>
    );

    const renderGalpHeader = (onBack: () => void) => (
      <div style={{
        position: 'relative',
        background: '#f7f4ed',
        minHeight: '95px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 16px',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
      >
        <button
          type="button"
          aria-label="back"
          onClick={onBack}
          style={{
            position: 'absolute',
            left: '16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 1L1 6L6 11" stroke="#0b0b0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <img src="/companies/GALP/main_logo_galp.svg" alt="Galp" style={{ height: '30px', display: 'block' }} />
        <div style={{ position: 'absolute', right: '16px', top: '18px' }}>
          <button
            type="button"
            onClick={() => setGalpLangOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontFamily: theme.bodyFont,
            }}
          >
            <img src={currentLang.flag} alt="" style={{ width: '21px', height: '17px', display: 'block' }} />
            <span style={{ fontSize: '14px', color: '#0b0b0b' }}>{currentLang.code}</span>
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 5L9 1" stroke="#0b0b0b" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {galpLangOpen && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
              zIndex: 10,
              overflow: 'hidden',
              minWidth: '92px',
            }}
            >
              {galpLanguages.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  onClick={() => handleGalpLangChange(l.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '8px 12px',
                    background: l.value === galpLang ? '#f7f4ed' : 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: theme.bodyFont,
                    fontSize: '14px',
                    color: '#0b0b0b',
                  }}
                >
                  <img src={l.flag} alt="" style={{ width: '21px', height: '17px', display: 'block' }} />
                  {l.code}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    // Galp billing / NIF details form (design 518-2478) — full screen, not a modal.
    if (galpBillingOpen) {
      const galpLabelStyle: React.CSSProperties = {
        fontFamily: theme.bodyFont, fontSize: '14px', fontWeight: 400, color: '#0b0b0b', margin: '0 0 6px', display: 'block',
      };
      const galpFieldWrap = (hasError?: boolean): React.CSSProperties => ({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        border: `1px solid ${hasError ? '#d32f2f' : '#bab9ba'}`,
        borderRadius: '10px',
        padding: '11px 14px',
        background: 'white',
        boxSizing: 'border-box',
        width: '100%',
      });
      const galpInputStyle: React.CSSProperties = {
        flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: theme.bodyFont, fontSize: '14px', color: '#0b0b0b',
      };
      const galpErrStyle: React.CSSProperties = { color: '#d32f2f', fontSize: '12px', margin: '4px 0 0', display: 'block' };
      const galpFlagBtn: React.CSSProperties = {
        background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '30px', height: '20px', borderRadius: '4px', overflow: 'hidden', flexShrink: 0, display: 'block', lineHeight: 0,
      };

      return (
        <div style={{
          width: '100%',
          height: '100dvh',
          background: 'white',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          fontFamily: theme.bodyFont,
        }}
        >
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={5000}
            open={notificationVisible}
            onClose={onCloseAlert}
          >
            <Alert onClose={onCloseAlert} severity={notificationClass as AlertColor} variant="filled" sx={{ width: '100%' }}>
              {i18n.t(notificationClass)}
            </Alert>
          </Snackbar>

          {renderGalpHeader(closeTaxpayerModal)}

          <TaxCountryModal
            isSelectingCountry={isSelectingCountry}
            setSelectingCountry={setSelectingCountry}
            handleCountrySelected={handleCountrySelected}
            country={country}
            variant="galp"
          />

          <div style={{
            flex: 1, overflowY: 'auto', padding: '24px 20px 28px', boxSizing: 'border-box',
          }}
          >
            {taxpayerLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '40px' }}>
                <CircularProgress size={40} style={{ color: theme.loadingIconColor }} variant="indeterminate" />
              </div>
            ) : (
              <form noValidate autoComplete="off" id="galp-taxpayer">
                {isAddressRequired && (
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="galp-name" style={galpLabelStyle}>{i18n.t('name')}</label>
                    <div style={galpFieldWrap(!!error?.name)}>
                      <input
                        id="galp-name"
                        style={galpInputStyle}
                        defaultValue={name}
                        placeholder={i18n.t('name') as string}
                        onChange={(e) => {
                          setName(e.target.value);
                          setError((prev) => ({ ...prev, name: e.target.value !== '' ? null : i18n.t('invalid_name') }));
                        }}
                      />
                    </div>
                    {error?.name && <span style={galpErrStyle}>{error.name}</span>}
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="galp-nif" style={galpLabelStyle}>{i18n.t('vat_number')}</label>
                  <div style={galpFieldWrap(!!error?.nif)}>
                    <button type="button" onClick={() => setSelectingCountry(true)} style={galpFlagBtn}>
                      <CountryFlag style={{ width: '30px', height: '20px', display: 'block' }} />
                    </button>
                    <input
                      id="galp-nif"
                      style={galpInputStyle}
                      value={vatNumber.number}
                      onChange={(e) => {
                        const number = e.target.value;
                        const nifError = getVatNumberError(vatNumber.prefix, number);
                        setVatNumber((prev) => ({ ...prev, number }));
                        setError((prev) => ({ ...prev, nif: nifError ? i18n.t(nifError) : null }));
                      }}
                    />
                  </div>
                  {error?.nif && <span style={galpErrStyle}>{error.nif}</span>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="galp-phone" style={galpLabelStyle}>{i18n.t('mobile_phone_number')}</label>
                  <div style={galpFieldWrap(!!error?.mobilePhone)}>
                    <button type="button" onClick={() => setSelectingCountry(true)} style={galpFlagBtn}>
                      <CountryFlag style={{ width: '30px', height: '20px', display: 'block' }} />
                    </button>
                    <input
                      id="galp-phone"
                      style={galpInputStyle}
                      value={phoneNumber}
                      placeholder="999 999 999"
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        const regionCode = getRegionCodeForCountryCode(countryData[country]?.code);
                        const parsedPhoneNumber = parsePhoneNumber(rawValue || '', regionCode);
                        const formattedPhoneNumber = parsedPhoneNumber.getNumber('international');
                        const tempVerification = new PhoneNumber(formattedPhoneNumber || '', countryRegionCode);
                        const valid = tempVerification.isValid() || (!formattedPhoneNumber && e.target.value === '');
                        setPhoneNumber(formattedPhoneNumber || e.target.value);
                        setError((prev) => ({ ...prev, mobilePhone: valid ? null : i18n.t('invalid_phone_number') }));
                      }}
                    />
                  </div>
                  {error?.mobilePhone && <span style={galpErrStyle}>{error.mobilePhone}</span>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="galp-email" style={galpLabelStyle}>{i18n.t('email')}</label>
                  <div style={galpFieldWrap(!!error?.email)}>
                    <input
                      id="galp-email"
                      style={galpInputStyle}
                      defaultValue={email}
                      placeholder={i18n.t('enter_valid_email') as string}
                      onChange={(e) => {
                        const valid = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(e.target.value) || e.target.value === '';
                        setEmail(e.target.value);
                        setError((prev) => ({ ...prev, email: valid ? null : i18n.t('invalid_email') }));
                      }}
                    />
                  </div>
                  {error?.email && <span style={galpErrStyle}>{error.email}</span>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="galp-street" style={galpLabelStyle}>{i18n.t('street_name')}</label>
                  <div style={galpFieldWrap(!!error?.streetName)}>
                    <input
                      id="galp-street"
                      style={galpInputStyle}
                      defaultValue={streetName}
                      placeholder={i18n.t('enter_valid_street') as string}
                      onChange={(e) => {
                        const { value } = e.target;
                        const isEmpty = value.trim().length === 0;
                        const isTooLong = value.length >= 250;
                        let errorMessage = null;
                        if (isAddressRequired && isEmpty) errorMessage = i18n.t('required_street_name');
                        else if (isTooLong) errorMessage = i18n.t('invalid_street_name');
                        setError((prev) => ({ ...prev, streetName: errorMessage }));
                        setStreetName(value);
                      }}
                    />
                  </div>
                  {error?.streetName && <span style={galpErrStyle}>{error.streetName}</span>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="galp-zip" style={galpLabelStyle}>{i18n.t('address_zip_code')}</label>
                  <div style={galpFieldWrap(!!error?.zipCode)}>
                    <input
                      id="galp-zip"
                      style={galpInputStyle}
                      defaultValue={zipCode}
                      placeholder="9999-999"
                      onChange={(e) => {
                        const { value } = e.target;
                        const zipCodeError = getPostalCodeError(country, value, { required: isAddressRequired });
                        setError((prev) => ({ ...prev, zipCode: zipCodeError ? i18n.t(zipCodeError) : null }));
                        setZipCode(value);
                      }}
                    />
                  </div>
                  {error?.zipCode && <span style={galpErrStyle}>{error.zipCode}</span>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="galp-city" style={galpLabelStyle}>{i18n.t('city_placeholder')}</label>
                  <div style={galpFieldWrap(!!error?.city)}>
                    <input
                      id="galp-city"
                      style={galpInputStyle}
                      defaultValue={city}
                      placeholder={i18n.t('city_placeholder') as string}
                      onChange={(e) => {
                        const isEmpty = e.target.value.length > 0;
                        setError((prev) => ({ ...prev, city: isAddressRequired && !isEmpty ? i18n.t('required_city') : null }));
                        setCity(e.target.value);
                      }}
                    />
                  </div>
                  {error?.city && <span style={galpErrStyle}>{error.city}</span>}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', margin: '4px 0 0' }}>
                  <Checkbox
                    color="primary"
                    checked={saveToStorage}
                    onChange={() => setSaveToStorage(!saveToStorage)}
                    style={{ padding: '0 8px 0 0' }}
                  />
                  <span style={{ fontFamily: theme.bodyFont, fontSize: '13px', color: '#0b0b0b' }}>{i18n.t('save_bill_data')}</span>
                </div>
              </form>
            )}
          </div>

          {/* Footer — divider + Save (design 518-2478) */}
          {!taxpayerLoading && (
            <div style={{
              borderTop: '1px solid #e6e6e6',
              background: 'white',
              padding: '16px 20px',
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
            >
              <button
                type="button"
                disabled={isFormInvalid()}
                onClick={saveTaxpayerDetails}
                style={{
                  height: '44px',
                  width: '100%',
                  borderRadius: '57px',
                  border: 'none',
                  background: isFormInvalid() ? '#e3a996' : theme.buttonColor,
                  color: 'white',
                  fontFamily: theme.bodyFont,
                  fontSize: '15px',
                  cursor: isFormInvalid() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
                }}
              >
                {taxpayerSaveLoading ? (
                  <CircularProgress size={18} style={{ color: 'white' }} variant="indeterminate" />
                ) : i18n.t('save')}
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        fontFamily: theme.bodyFont,
      }}
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={5000}
          open={notificationVisible}
          onClose={onCloseAlert}
        >
          <Alert
            onClose={onCloseAlert}
            severity={notificationClass as AlertColor}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {i18n.t(notificationClass)}
          </Alert>
        </Snackbar>

        {/* Header — back + Galp logo + language selector */}
        {renderGalpHeader(() => history.goBack())}

        {/* Body */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 16px 24px',
          gap: '16px',
          boxSizing: 'border-box',
        }}
        >
          {sessionInfo && (
            <p style={{
              fontFamily: theme.bodyFont,
              fontSize: '14px',
              fontWeight: 500,
              color: '#0b0b0b',
              margin: 0,
              textAlign: 'center',
            }}
            >
              {sessionInfo?.vendingMachine?.identificationNumber}
              {sessionInfo?.vendingMachine?.name ? ` | ${sessionInfo.vendingMachine.name}` : ''}
            </p>
          )}

          {isEVRelated && sessionInfo?.pin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontFamily: theme.bodyFont, fontSize: '14px', fontWeight: 500, color: '#0b0b0b', opacity: 0.8,
              }}
              >
                PIN:
              </span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #bab9ba',
                borderRadius: '10px',
                padding: '6px 12px',
                fontFamily: theme.bodyFont,
                fontSize: '12px',
                fontWeight: 500,
                color: '#111110',
              }}
              >
                {sessionInfo.pin}
                <img
                  src="/companies/GALP/pin_copy_icon.svg"
                  alt=""
                  style={{ width: '13px', height: '15px', display: 'block' }}
                />
              </span>
            </div>
          )}

          <div style={{ width: '100%', height: '1px', background: '#eee', flexShrink: 0 }} />

          {isEVRelated && (
            <div style={{
              position: 'relative',
              width: '120px',
              height: '120px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            >
              <CircularProgress
                size={120}
                thickness={3}
                sx={{ color: '#be2e00' }}
                variant={isCharging ? 'indeterminate' : 'determinate'}
                value={isCharging ? undefined : 100}
              />
              <span style={{
                position: 'absolute',
                left: 0,
                right: 0,
                fontFamily: theme.bodyFont,
                fontSize: '12px',
                fontWeight: 700,
                color: '#0b0b0b',
                textAlign: 'center',
                whiteSpace: isCharging ? 'nowrap' : 'normal',
              }}
              >
                {isCharging
                  ? (i18n.t('charging') as string).replace(/[.…\s]+$/, '')
                  : i18n.t('charging_complete')}
              </span>
            </div>
          )}

          {isEVRelated && chargingData && (
            <div style={{ width: '100%', display: 'flex', gap: '12px' }}>
              {chargingData.kWh !== undefined && (
                <div style={galpCardStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={galpCardLabel}>{i18n.t('power')}</span>
                    <span style={galpCardValue}>{`${chargingData.kWh ?? '---'} kWh`}</span>
                  </div>
                  <img
                    src="/companies/GALP/power_icon.svg"
                    alt=""
                    style={{ width: '10px', height: '16px', display: 'block', flexShrink: 0 }}
                  />
                </div>
              )}
              {chargingData.chargingTime !== undefined && (
                <div style={galpCardStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={galpCardLabel}>{(i18n.t('duration') as string).replace(/[:\s]+$/, '')}</span>
                    <span style={galpCardValue}>{durationText}</span>
                  </div>
                  <img
                    src="/companies/GALP/duration_icon.svg"
                    alt=""
                    style={{ width: '16px', height: '16px', display: 'block', flexShrink: 0 }}
                  />
                </div>
              )}
              {chargingData.price !== undefined && (
                <div style={galpCardStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={galpCardLabel}>{(i18n.t('price') as string).replace(/[:\s]+$/, '')}</span>
                    <span style={galpCardValue}>
                      {chargingData.price !== null && chargingData.price !== undefined
                        ? `${(chargingData.price / 100).toFixed(2)} €` : '---'}
                    </span>
                  </div>
                  <img
                    src="/companies/GALP/price_icon.svg"
                    alt=""
                    style={{ width: '17px', height: '16px', display: 'block', flexShrink: 0 }}
                  />
                </div>
              )}
            </div>
          )}

          {isEVRelated && theme.graphic && Array.isArray(chartData) && chartData.length > 0 && (
            <div style={{
              width: '100%', marginTop: '4px', padding: '0 10px', boxSizing: 'border-box',
            }}
            >
              <PowerChart theme={theme} data={chartData} variant="galp" />
            </div>
          )}
        </div>

        {/* Footer — Terminar + Recibo */}
        <div style={{
          borderTop: '1px solid #bababa',
          background: 'white',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '20px 32px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          {isEVRelated && isCharging && (
            <button
              type="button"
              disabled={loadingTerminate}
              onClick={async () => {
                setLoadingTerminate(true);
                await closeSession(sessionInfo?.sessionId);
              }}
              style={{
                height: '36px',
                borderRadius: '57px',
                background: theme.buttonColor,
                border: 'none',
                color: theme.buttonTextColor || 'white',
                fontFamily: theme.bodyFont,
                fontSize: '14px',
                cursor: loadingTerminate ? 'not-allowed' : 'pointer',
                boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
              }}
            >
              {loadingTerminate ? (
                <CircularProgress size={16} style={{ color: 'white' }} variant="indeterminate" />
              ) : i18n.t('terminate')}
            </button>
          )}
          {isEVRelated && isCharging && galpAuthDoc
            && renderGalpDocButton(
              'galp-auth',
              i18n.t('authorization_receipt'),
              () => openGalpBilling(),
            )}
          {isEVRelated && !isCharging
            && galpOrderedDocs.map((d) => renderGalpDocButton(
              d.id || d.type,
              galpDocLabel(d.type),
              () => (isAuthDoc(d.type) ? openGalpBilling() : handleDocumentDownload(d)),
            ))}
        </div>
      </div>
    );
  }

  if (isHelexia) {
    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={5000}
          open={notificationVisible}
          onClose={onCloseAlert}
        >
          <Alert
            onClose={onCloseAlert}
            severity={notificationClass as AlertColor}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {i18n.t(notificationClass)}
          </Alert>
        </Snackbar>

        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          open={showTaxpayerModal}
          onClose={closeTaxpayerModal}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 500 } }}
        >
          <Fade in={showTaxpayerModal}>
            <Box sx={style} paddingTop={0} style={{ maxHeight: '90dvh', overflowY: 'scroll' }}>
              <IconButton
                onClick={closeTaxpayerModal}
                sx={{ position: 'absolute', top: 0, left: 'calc(80dvw - 18px)' }}
                size="medium"
              >
                <CloseIcon style={{ padding: 0, color: theme.endChargeButtonColor }} />
              </IconButton>
              {taxpayerLoading ? (
                <CircularProgress size={50} style={{ color: theme.loadingIconColor }} variant="indeterminate" />
              ) : (
                <>
                  <Typography style={{ textAlign: 'center', color: theme.titleColor }} id="transition-modal-title" variant="h6" component="h2">
                    {i18n.t('insert_taxpayer_details')}
                  </Typography>
                  <TaxCountryModal
                    isSelectingCountry={isSelectingCountry}
                    setSelectingCountry={setSelectingCountry}
                    handleCountrySelected={handleCountrySelected}
                    country={country}
                  />
                  <FormContainer style={{ fontFamily: theme.bodyFont }}>
                    <form noValidate autoComplete="off" id="taxpayer">
                      <StyledDiv style={{ paddingTop: '10px' }}>
                        <HelexiaInputWrapper style={error?.name ? { outline: '1px solid #d32f2f' } : {}}>
                          <HelexiaInput
                            id="name"
                            required={isAddressRequired}
                            onChange={(e) => {
                              setName(e.target.value);
                              if (isAddressRequired) {
                                setError((prev) => ({
                                  ...prev,
                                  name: e.target.value !== '' ? null : i18n.t('invalid_name'),
                                }));
                              }
                            }}
                            placeholder={i18n.t('name') as string}
                            defaultValue={name}
                          />
                        </HelexiaInputWrapper>
                        {error?.name && <span style={{ color: '#d32f2f', fontSize: '12px' }}>{error.name}</span>}
                      </StyledDiv>
                      <StyledDiv>
                        <HelexiaInputWrapper style={error?.nif ? { outline: '1px solid #d32f2f' } : {}}>
                          <button
                            type="button"
                            onClick={() => setSelectingCountry(true)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '25px', height: '25px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, position: 'relative' }}
                          >
                            <CountryFlag style={{ width: '30px', height: '20px', display: 'block' }} />
                          </button>
                          <HelexiaInput
                            id="nif"
                            placeholder={i18n.t('vat_number') as string}
                            onChange={(e) => {
                              const number = e.target.value;
                              const nifError = getVatNumberError(vatNumber.prefix, number);
                              setVatNumber((prev) => ({ ...prev, number }));
                              setError((prev) => ({ ...prev, nif: nifError ? i18n.t(nifError) : null }));
                            }}
                            value={vatNumber.number}
                          />
                        </HelexiaInputWrapper>
                        {error?.nif && <span style={{ color: '#d32f2f', fontSize: '12px' }}>{error.nif}</span>}
                      </StyledDiv>
                      <StyledDiv>
                        <HelexiaInputWrapper style={error?.mobilePhone ? { outline: '1px solid #d32f2f' } : {}}>
                          <button
                            type="button"
                            onClick={() => setSelectingCountry(true)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '25px', height: '25px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, position: 'relative' }}
                          >
                            <CountryFlag style={{ width: '30px', height: '20px', display: 'block' }} />
                          </button>
                          <HelexiaInput
                            id="mobilePhone"
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              const regionCode = getRegionCodeForCountryCode(countryData[country]?.code);
                              const parsedPhoneNumber = parsePhoneNumber(rawValue || '', regionCode);
                              const formattedPhoneNumber = parsedPhoneNumber.getNumber('international');
                              const tempVerification = new PhoneNumber(formattedPhoneNumber || '', countryRegionCode);
                              const valid = tempVerification.isValid() || (!formattedPhoneNumber && e.target.value === '');
                              setPhoneNumber(formattedPhoneNumber || e.target.value);
                              setError((prev) => ({ ...prev, mobilePhone: valid ? null : i18n.t('invalid_phone_number') }));
                            }}
                            value={phoneNumber}
                            placeholder={i18n.t('mobile_phone_number') as string}
                          />
                        </HelexiaInputWrapper>
                        {error?.mobilePhone && <span style={{ color: '#d32f2f', fontSize: '12px' }}>{error.mobilePhone}</span>}
                      </StyledDiv>
                      <StyledDiv>
                        <HelexiaInputWrapper style={error?.email ? { outline: '1px solid #d32f2f' } : {}}>
                          <HelexiaInput
                            id="email"
                            onChange={(e) => {
                              const valid = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(e.target.value) || e.target.value === '';
                              setEmail(e.target.value);
                              setError((prev) => ({ ...prev, email: valid ? null : i18n.t('invalid_email') }));
                            }}
                            placeholder={i18n.t('email') as string}
                            defaultValue={email}
                          />
                        </HelexiaInputWrapper>
                        {error?.email && <span style={{ color: '#d32f2f', fontSize: '12px' }}>{error.email}</span>}
                      </StyledDiv>
                      <StyledDiv>
                        <HelexiaInputWrapper style={error?.streetName ? { outline: '1px solid #d32f2f' } : {}}>
                          <HelexiaInput
                            id="streetName"
                            onChange={(e) => {
                              const { value } = e.target;
                              const isEmpty = value.trim().length === 0;
                              const isTooLong = value.length >= 250;
                              let errorMessage = null;
                              if (isAddressRequired && isEmpty) errorMessage = i18n.t('required_street_name');
                              else if (isTooLong) errorMessage = i18n.t('invalid_street_name');
                              setError((prev) => ({ ...prev, streetName: errorMessage }));
                              setStreetName(value);
                            }}
                            placeholder={(i18n.t('address') as string) || ''}
                            defaultValue={streetName}
                          />
                        </HelexiaInputWrapper>
                        {error?.streetName && <span style={{ color: '#d32f2f', fontSize: '12px' }}>{error.streetName}</span>}
                      </StyledDiv>
                      <StyledDiv>
                        <HelexiaInputWrapper style={error?.zipCode ? { outline: '1px solid #d32f2f' } : {}}>
                          <HelexiaInput
                            id="zipCode"
                            onChange={(e) => {
                              const { value } = e.target;
                              const zipCodeError = getPostalCodeError(country, value, { required: isAddressRequired });
                              setError((prev) => ({ ...prev, zipCode: zipCodeError ? i18n.t(zipCodeError) : null }));
                              setZipCode(value);
                            }}
                            placeholder={i18n.t('address_zip_code') as string}
                            defaultValue={zipCode}
                          />
                        </HelexiaInputWrapper>
                        {error?.zipCode && <span style={{ color: '#d32f2f', fontSize: '12px' }}>{error.zipCode}</span>}
                      </StyledDiv>
                      <StyledDiv>
                        <HelexiaInputWrapper style={error?.city ? { outline: '1px solid #d32f2f' } : {}}>
                          <HelexiaInput
                            id="city"
                            onChange={(e) => {
                              const isEmpty = e.target.value.length > 0;
                              setError((prev) => ({
                                ...prev,
                                city: isAddressRequired && !isEmpty ? i18n.t('required_city') : null,
                              }));
                              setCity(e.target.value);
                            }}
                            placeholder={i18n.t('city_placeholder') as string}
                            defaultValue={city}
                          />
                        </HelexiaInputWrapper>
                        {error?.city && <span style={{ color: '#d32f2f', fontSize: '12px' }}>{error.city}</span>}
                      </StyledDiv>
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '0px 0px 30px 0px' }}>
                        <Checkbox color="primary" checked={saveToStorage} onChange={() => setSaveToStorage(!saveToStorage)} />
                        <span>{i18n.t('save_bill_data')}</span>
                      </div>
                      <ButtonsContainer>
                        <button
                          type="button"
                          disabled={isFormInvalid()}
                          onClick={saveTaxpayerDetails}
                          style={{
                            background: isFormInvalid() ? '#d9d9d9' : 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)',
                            borderRadius: '12px',
                            height: '36px',
                            border: 'none',
                            cursor: isFormInvalid() ? 'not-allowed' : 'pointer',
                            color: isFormInvalid() ? '#8c8c8c' : 'white',
                            fontFamily: 'Geologica, sans-serif',
                            fontWeight: 400,
                            fontSize: '14px',
                            width: '300px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {taxpayerSaveLoading ? (
                            <CircularProgress size={20} style={{ color: 'white' }} variant="indeterminate" />
                          ) : i18n.t('save')}
                        </button>
                      </ButtonsContainer>
                    </form>
                  </FormContainer>
                </>
              )}
            </Box>
          </Fade>
        </Modal>

        <TopHeader showVm userLogo showShadow />

        <div style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 24px 66px',
          gap: '24px',
        }}
        >
          {/* Charging circle — EV active */}
          {isEVRelated && isCharging && (
            <div style={{ position: 'relative', width: '150px', height: '150px', flexShrink: 0 }}>
              <HelexiaSpinningCircle />
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              >
                <span style={{
                  fontFamily: 'Geologica, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#004e74',
                  textAlign: 'center',
                }}
                >
                  {i18n.t('charging')}
                </span>
                <button
                  type="button"
                  disabled={loadingTerminate}
                  onClick={async () => {
                    setLoadingTerminate(true);
                    await closeSession(sessionInfo?.sessionId);
                  }}
                  style={{
                    background: loadingTerminate ? '#d9d9d9' : 'red',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '4px 16px',
                    fontFamily: 'Geologica, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    cursor: loadingTerminate ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loadingTerminate ? (
                    <CircularProgress size={14} style={{ color: 'white' }} variant="indeterminate" />
                  ) : 'Terminar'}
                </button>
              </div>
            </div>
          )}

          {/* Charging complete */}
          {isEVRelated && !isCharging && (
            <span style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              color: '#004e74',
              textAlign: 'center',
            }}
            >
              {i18n.t('charging_complete')}
            </span>
          )}

          {/* Non-EV */}
          {!isEVRelated && (
            <span style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              color: '#004e74',
              textAlign: 'center',
            }}
            >
              {i18n.t('thanks_for_purchase')}
            </span>
          )}

          {/* Session title */}
          <p style={{
            fontFamily: 'Geologica, sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '16px',
            letterSpacing: '-0.98px',
            color: '#014f75',
            textAlign: 'center',
            margin: 0,
            width: '100%',
            wordBreak: 'break-word',
          }}
          >
            {sessionInfo?.vendingMachine?.identificationNumber}
            {sessionInfo?.vendingMachine?.name ? ` | ${sessionInfo.vendingMachine.name}` : ''}
          </p>

          {/* PIN */}
          {isEVRelated && sessionInfo?.pin && (
            <p style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '20px',
              letterSpacing: '-0.6px',
              color: '#014f75',
              textAlign: 'center',
              margin: 0,
            }}
            >
              {sessionInfo.pin}
            </p>
          )}

          {/* Stats cards */}
          {chargingData && (
            <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', justifyContent: 'center', width: '100%' }}>
              {chargingData.kWh !== undefined && (
                <div style={{
                  background: '#dcdcdc',
                  borderRadius: '8px',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  minWidth: '80px',
                  gap: '4px',
                }}
                >
                  <img src={helexiaChargingIcon} alt="" style={{ width: '15px' }} />
                  <span style={{ fontFamily: 'Geologica, sans-serif', fontSize: '12px', color: '#004d74', fontWeight: 500, marginTop: '4px' }}>
                    kWh:
                  </span>
                  <span style={{ fontFamily: 'Geologica, sans-serif', fontSize: '12px', color: '#004d74', fontWeight: 400 }}>
                    {chargingData.kWh !== null && chargingData.kWh !== undefined ? chargingData.kWh : '---'}
                  </span>
                </div>
              )}
              {chargingData.chargingTime !== undefined && (
                <div style={{
                  background: '#dcdcdc',
                  borderRadius: '8px',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  minWidth: '80px',
                  gap: '4px',
                }}
                >
                  <img src={helexiaDurationIcon} alt="" style={{ width: '22px' }} />
                  <span style={{ fontFamily: 'Geologica, sans-serif', fontSize: '12px', color: '#004d74', fontWeight: 500, marginTop: '4px' }}>
                    {i18n.t('duration')}
                  </span>
                  <span style={{ fontFamily: 'Geologica, sans-serif', fontSize: '12px', color: '#004d74', fontWeight: 400 }}>
                    {chargingData.chargingTime !== null && time !== null
                      ? moment.utc(time.asMilliseconds()).format('HH:mm:ss') : '---'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Introduzir Dados de Faturação */}
          {canEditTaxDetails && (
            <button
              type="button"
              onClick={openTaxpayerModal}
              style={{
                background: 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)',
                borderRadius: '12px',
                height: '36px',
                border: 'none',
                cursor: 'pointer',
                color: 'white',
                fontFamily: 'Geologica, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                letterSpacing: '-0.42px',
                width: '100%',
                flexShrink: 0,
              }}
            >
              {i18n.t('insert_taxpayer_details')}
            </button>
          )}

          {/* Document download buttons */}
          {sessionInfo?.documents?.map((documentInfo: DocumentInfo) => (
            <button
              key={documentInfo.type}
              type="button"
              onClick={() => handleDocumentDownload(documentInfo)}
              style={{
                background: 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)',
                borderRadius: '12px',
                height: '36px',
                border: 'none',
                cursor: 'pointer',
                color: 'white',
                fontFamily: 'Geologica, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                letterSpacing: '-0.42px',
                width: '100%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <span style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              >
                <DownloadIcon style={{ color: theme.endChargeButtonColor, fontSize: '16px' }} />
              </span>
              {documentInfo.type === 'CUSTOMER' ? i18n.t('payment_document') : i18n.t('invoice')}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100dvh', overflowY: 'scroll' }}>
      <Root>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={5000}
          open={notificationVisible}
          onClose={onCloseAlert}
        >
          <Alert
            onClose={onCloseAlert}
            severity={notificationClass as AlertColor}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {i18n.t(notificationClass)}
          </Alert>
        </Snackbar>
        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          open={showTaxpayerModal}
          onClose={closeTaxpayerModal}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
            },
          }}
        >
          <Fade in={showTaxpayerModal}>
            <Box sx={style} paddingTop={0} style={{ maxHeight: '90dvh', overflowY: 'scroll' }}>
              <IconButton
                onClick={closeTaxpayerModal}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 'calc(80dvw - 18px)',
                }}
                size="medium"
              >
                <CloseIcon style={{ padding: 0, color: 'red' }} />
              </IconButton>

              {
                  taxpayerLoading ? (
                    <CircularProgress
                      size={50}
                      style={{ color: theme.loadingIconColor }}
                      variant="indeterminate"
                    />
                  ) : (
                    <>
                      <Typography
                        style={{ textAlign: 'center' }}
                        id="transition-modal-title"
                        variant="h6"
                        component="h2"
                      >
                        {i18n.t('insert_taxpayer_details')}
                      </Typography>
                      <TaxCountryModal
                        isSelectingCountry={isSelectingCountry}
                        setSelectingCountry={setSelectingCountry}
                        handleCountrySelected={handleCountrySelected}
                        country={country}
                      />
                      <FormContainer style={{ fontFamily: theme.bodyFont }}>
                        <form noValidate autoComplete="off" id="taxpayer">
                          <StyledDiv style={{ paddingTop: '10px' }}>
                            <span>{i18n.t('name')}</span>
                            <StyledTextField
                              fullWidth
                              id="name"
                              required={isAddressRequired}
                              onChange={(e) => {
                                setName(e.target.value);
                                if (isAddressRequired) {
                                  const valid = e.target.value !== '';

                                  setError((prevState) => ({
                                    ...prevState,
                                    name: valid ? null : i18n.t('invalid_name'),
                                  }));
                                }
                              }}
                              placeholder={i18n.t('name_placeholder') as string}
                              error={!!error?.name}
                              defaultValue={name}
                              helperText={error.name ? error.name : ''}
                            />
                          </StyledDiv>
                          <StyledDiv>
                            <span>{i18n.t('vat_number')}</span>
                            <StyledTextField
                              style={{ paddingTop: '5px' }}
                              fullWidth
                              id="nif"
                              placeholder="255555555"
                              onChange={(e) => {
                                const number = e.target.value;
                                const nifError = getVatNumberError(vatNumber.prefix, number);

                                setVatNumber((prev) => ({ ...prev, number }));
                                setError((prevState) => ({
                                  ...prevState,
                                  nif: nifError ? i18n.t(nifError) : null,
                                }));
                              }}
                              value={vatNumber.number}
                              error={!!error?.nif}
                              helperText={error.nif ? error.nif : ''}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment
                                    style={{
                                      width: '50px',
                                      paddingBottom: '5px',
                                    }}
                                    position="start"
                                    onClick={() => setSelectingCountry(true)}
                                  >
                                    <CountryFlag
                                      style={{
                                        borderRadius: '5px',
                                        width: '100%',
                                      }}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </StyledDiv>
                          <StyledDiv>
                            <span>{i18n.t('mobile_phone_number')}</span>
                            <StyledTextField
                              style={{ paddingTop: '5px' }}
                              fullWidth
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                const regionCode = getRegionCodeForCountryCode(countryData[country]?.code);
                                const parsedPhoneNumber = parsePhoneNumber(rawValue || '', regionCode);
                                const formattedPhoneNumber = parsedPhoneNumber.getNumber('international');
                                const tempVerification = new PhoneNumber(formattedPhoneNumber || '', countryRegionCode);

                                const valid = tempVerification.isValid()
                                  || (!formattedPhoneNumber && e.target.value === '');
                                setPhoneNumber(formattedPhoneNumber || e.target.value);
                                setError((prevState) => ({
                                  ...prevState,
                                  mobilePhone: valid
                                    ? null : i18n.t('invalid_phone_number'),
                                }));
                              }}
                              id="mobilePhone"
                              value={phoneNumber}
                              placeholder="999 999 999"
                              error={!!error?.mobilePhone}
                              helperText={error.mobilePhone ? error.mobilePhone : ''}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment
                                    style={{
                                      width: '50px',
                                      paddingBottom: '5px',
                                    }}
                                    position="start"
                                    onClick={() => setSelectingCountry(true)}
                                  >
                                    <CountryFlag
                                      style={{
                                        borderRadius: '5px',
                                        width: '100%',
                                      }}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </StyledDiv>
                          <StyledDiv>
                            <span>{i18n.t('email')}</span>
                            <StyledTextField
                              style={{ paddingTop: '5px', paddingBottom: '5px' }}
                              fullWidth
                              id="email"
                              onChange={(e) => {
                                const valid = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(e.target.value)
                                  || e.target.value === '';
                                setEmail(e.target.value);
                                setError((prevState) => ({
                                  ...prevState,
                                  email: valid ? null : i18n.t('invalid_email'),
                                }));
                              }}
                              placeholder={i18n.t('email_placeholder') as string}
                              error={!!error?.email}
                              defaultValue={email}
                              helperText={error.email ? error.email : ''}
                            />
                          </StyledDiv>
                          <StyledDiv>
                            <span>{i18n.t('street_name')}</span>
                            <StyledTextField
                              style={{ paddingTop: '5px', paddingBottom: '5px' }}
                              fullWidth
                              id="streetName"
                              onChange={(e) => {
                                const { value } = e.target;
                                const isEmpty = value.trim().length === 0;
                                const isTooLong = value.length >= 250;

                                let errorMessage = null;

                                if (isAddressRequired && isEmpty) {
                                  errorMessage = i18n.t('required_street_name');
                                } else if (isTooLong) {
                                  errorMessage = i18n.t('invalid_street_name');
                                }

                                setError((prevState) => ({
                                  ...prevState,
                                  streetName: errorMessage,
                                }));
                                setStreetName(value);
                              }}
                              placeholder={(i18n.t('street_name_placeholder') as string) || ''}
                              error={!!error?.streetName}
                              defaultValue={streetName}
                              helperText={error.streetName ? error.streetName : ''}
                            />
                          </StyledDiv>
                          <StyledDiv>
                            <span>{i18n.t('address_zip_code')}</span>
                            <StyledTextField
                              style={{ paddingTop: '5px', paddingBottom: '5px' }}
                              fullWidth
                              id="zipCode"
                              onChange={(e) => {
                                const { value } = e.target;
                                const zipCodeError = getPostalCodeError(country, value, { required: isAddressRequired });

                                setError((prevState) => ({
                                  ...prevState,
                                  zipCode: zipCodeError ? i18n.t(zipCodeError) : null,
                                }));

                                setZipCode(value);
                              }}
                              placeholder="9999-999"
                              error={!!error?.zipCode}
                              defaultValue={zipCode}
                              helperText={error.zipCode ? error.zipCode : ''}
                            />
                          </StyledDiv>
                          <StyledDiv>
                            <span>{i18n.t('address_city')}</span>
                            <StyledTextField
                              style={{ paddingTop: '5px', paddingBottom: '5px' }}
                              fullWidth
                              id="city"
                              onChange={(e) => {
                                const isEmpty = e.target.value.length > 0;
                                setError((prev) => ({
                                  ...prev,
                                  city: isAddressRequired && !isEmpty ? i18n.t('required_city') : null,
                                }));
                                setCity(e.target.value);
                              }}
                              placeholder={i18n.t('city_placeholder') as string}
                              error={!!error?.city}
                              defaultValue={city}
                              helperText={error.city ? error.city : ''}
                            />
                          </StyledDiv>
                          <div
                            style={{
                              display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '10px 0',
                            }}
                          >
                            <Checkbox
                              color="primary"
                              checked={saveToStorage}
                              onChange={() => setSaveToStorage(!saveToStorage)}
                            />
                            <span>{i18n.t('save_bill_data')}</span>
                          </div>
                          <ButtonsContainer>
                            <StyledButton
                              variant="outlined"
                              color={theme.buttonColor}
                              textColor={theme.buttonTextColor}
                              font={theme.bodyFont}
                              disabled={
                                isFormInvalid()
                              }
                              onClick={saveTaxpayerDetails}
                            >
                              {taxpayerSaveLoading ? (
                                <CircularProgress
                                  size={20}
                                  style={{ color: theme.loadingIconColor || 'white' }}
                                  variant="indeterminate"
                                />
                              ) : i18n.t('save')}
                            </StyledButton>
                          </ButtonsContainer>
                        </form>
                      </FormContainer>
                    </>
                  )
                }

            </Box>
          </Fade>
        </Modal>
        {sessionInfo && (
        <HeaderName color={theme.titleColor} font={theme.titleFont}>
          {sessionInfo?.vendingMachine?.identificationNumber}
          {' '}
          |
          {' '}
          {sessionInfo?.vendingMachine?.name}
        </HeaderName>
        )}
        {!isEVRelated && <Message>{i18n.t('thanks_for_purchase')}</Message>}
        {isEVRelated && (
        <>
          <StyledSpanPin color={theme.pinTextColor} font={theme.bodyFont}>
            PIN:
            {sessionInfo?.pin}
          </StyledSpanPin>
          <Box sx={{
            position: 'relative', display: 'inline-flex', marginBottom: '30px', marginTop: '10px',
          }}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {theme.loadingIcon ? (
            // loading icon
              [100, 0].includes(sessionInfo?.state) ? (
                <LoadingWrapper>
                  <StyledLoading
                    src={theme.loadingIcon}
                    alt="logo"
                  />
                </LoadingWrapper>
              ) : (
              // charged icon
                <img
                  src={theme.loadingIcon}
                  alt="logo"
                />
              )
            ) : (
              <CircularProgress
                size={200}
                sx={{
                  color: theme.loadingIconColor,
                  animationDuration: '80000ms',
                }}
                thickness={1}
                variant={[100, 0].includes(sessionInfo?.state) ? 'indeterminate' : 'determinate'}
                value={[100, 0].includes(sessionInfo?.state) ? undefined : 100}
              />
            )}
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  color: theme.titleColor,
                  textAlign: 'center',
                  width: '75%',
                  font: `1.1em ${theme.bodyFont}`,
                }}
              >
                {[100, 0].includes(sessionInfo?.state) ? i18n.t('charging') : i18n.t('charging_complete')}
              </span>
              {[100, 0].includes(sessionInfo?.state)
                && (
                  <ButtonsContainer>
                    <StyledButton2
                      variant="outlined"
                      color={theme.endChargeButtonColor}
                      textColor={theme.buttonTextColor}
                      font={theme.bodyFont}
                      disabled={loadingTerminate}
                      onClick={async () => {
                        setLoadingTerminate(true);
                        await closeSession(sessionInfo?.sessionId);
                      }}
                    >
                      Terminar
                    </StyledButton2>
                  </ButtonsContainer>
                )}
            </Box>
          </Box>
          {
          chargingData && (
            <div style={{ width: 'calc(100% - 24px)' }}>
              <Row>
                {
                  chargingData.kWh !== undefined && (
                    <StyledCard
                      elevation={0}
                      style={{ backgroundColor: theme.cardColor }}
                    >
                      {theme.EvStationIcon ? (
                        <img
                          src={theme.EvStationIcon}
                          alt="logo"
                        />
                      ) : (
                        <EvStationIcon
                          fontSize="large"
                          sx={{ color: theme.cardIconColor }}
                        />
                      )}
                      <StyledSpan
                        color={theme.cardTextColor}
                        font={theme.bodyFont}
                      >
                        kWh:
                      </StyledSpan>
                      <StyledP
                        color={theme.cardTextColor}
                        font={theme.bodyFont}
                      >
                        {chargingData?.kWh !== null && chargingData?.kWh !== undefined
                          ? chargingData.kWh : '---'}
                      </StyledP>
                    </StyledCard>
                  )
                }
                {
                  chargingData.chargingTime !== undefined && (
                    <StyledCard
                      elevation={0}
                      style={{ backgroundColor: theme.cardColor }}
                    >
                      {theme.TimerIcon ? (
                        <img
                          src={theme.TimerIcon}
                          alt="logo"
                        />
                      ) : (
                        <TimerIcon
                          fontSize="large"
                          sx={{ color: theme.cardIconColor }}
                        />
                      )}
                      <StyledSpan
                        color={theme.cardTextColor}
                        font={theme.bodyFont}
                      >
                        {i18n.t('duration')}
                      </StyledSpan>
                      <StyledP
                        color={theme.cardTextColor}
                        font={theme.bodyFont}
                      >
                        {chargingData?.chargingTime !== null && true && time !== null
                          ? moment.utc(time.asMilliseconds()).format('HH:mm:ss') : '---'}
                      </StyledP>
                    </StyledCard>
                  )
                }
                {
                  (chargingData?.price !== undefined) && (
                    <StyledCard
                      elevation={0}
                      style={{ backgroundColor: theme.cardColor }}
                    >
                      {theme.EuroIcon ? (
                        <img
                          src={theme.EuroIcon}
                          alt="logo"
                        />
                      ) : (
                        <EuroIcon
                          fontSize="large"
                          sx={{ color: theme.cardIconColor }}
                        />
                      )}
                      <StyledSpan
                        color={theme.cardTextColor}
                        font={theme.bodyFont}
                      >
                        {i18n.t('price')}
                      </StyledSpan>
                      <StyledP
                        color={theme.cardTextColor}
                        font={theme.bodyFont}
                      >
                        {chargingData.price !== null && true
                          ? `${(chargingData.price / 100).toFixed(2)}€`
                          : '---'}
                      </StyledP>
                    </StyledCard>
                  )
                }
              </Row>
              {theme.graphic && Array.isArray(chartData) && chartData.length > 0 && (
                <Row style={{ marginTop: '20px' }}>
                  <PowerChart theme={theme} data={chartData} />
                </Row>
              )}
            </div>
          )
        }
          <DocumentsContainer>
            {canEditTaxDetails && (
            <ButtonsContainer>
              <StyledButton
                variant="outlined"
                color={theme.buttonColor}
                textColor={theme.buttonTextColor}
                font={theme.bodyFont}
                onClick={openTaxpayerModal}
              >
                {i18n.t('insert_taxpayer_details')}
              </StyledButton>
            </ButtonsContainer>
            )}
          </DocumentsContainer>
        </>
        )}
        <DocumentsContainer>
          {sessionInfo && sessionInfo.documents.length === 0 && (
            <ButtonsContainer style={{ flexDirection: 'column', alignItems: 'center' }}>
              <StyledButton
                variant="outlined"
                disabled
                color={theme.buttonColor}
                textColor={theme.buttonTextColor}
                font={theme.bodyFont}
              >
                <DownloadIcon
                  style={{
                    color: theme.buttonTextColor,
                    margin: '0 10px 0 0',
                  }}
                />
                {i18n.t('payment_document')}
              </StyledButton>
              <span
                style={{
                  font: `0.85em ${theme.bodyFont}`,
                  color: theme.titleColor,
                  marginTop: 4,
                }}
              >
                {i18n.t('generating_document')}
              </span>
            </ButtonsContainer>
          )}
          {sessionInfo && sessionInfo.documents.map((documentInfo: DocumentInfo) => (
            <ButtonsContainer>
              <StyledButton
                variant="outlined"
                color={theme.buttonColor}
                textColor={theme.buttonTextColor}
                font={theme.bodyFont}
                onClick={() => handleDocumentDownload(documentInfo)}
              >
                <DownloadIcon
                  style={{
                    color: theme.buttonTextColor,
                    margin: '0 10px 0 0',
                  }}
                />
                {documentInfo.type === 'CUSTOMER' ? i18n.t('payment_document') : i18n.t('invoice')}
              </StyledButton>
            </ButtonsContainer>
          ))}
        </DocumentsContainer>
      </Root>
      {theme.logo && (
        <LogoFooter />
      )}
    </div>
  );
};

export default SessionDocumentsView;
